import { QUESTION_TEST_TYPES } from '../const/question-types'
import MarkdownBodyParser from '../../../lib/MarkdownBodyParser'
import QuestionType from '../lib/QuestionType'
import questionParser from '../../../helpers/question-parser'
import AnswerItem from '../../../common/AnswerItem'
import { IQuestionSkill } from '../types'
import MarkdownQuestionParser from '../../../lib/MarkdownQuestionParser'
import { QuestionFieldType } from '../../../common/QuestionFieldType'
import { max } from 'lodash'
import { IPrompts } from "../../../types";
import Prompt from "./Prompt";
import { CsvAnswerParser } from '../../../utils/CsvAnswerParser'

export default class Question extends QuestionFieldType {
  public id: number
  public index: number
  private _body: string
  public length: number
  private formLineStart: number
  private formLineEnd: number
  public test: boolean
  public answerItems: AnswerItem[] = []
  public group: number
  public description: string = ''
  public skills: IQuestionSkill = {}
  public prompts: IPrompts[] = []

  // textareaしか使われていません
  public textareaRowNumber: number = 0

  constructor(index: number, body: string = '', group: number = 0) {
    super('')
    this.index = index
    this.group = group
    this._body = body
    this.length = body.split('\n').length

    this.parseQuestion(this.questionBody)
    this.parseMetadata(this.metadataBody)
  }

  public updateBody() {
    this._body = this.questionBody
    if (!this.description && Object.keys(this.skills).length == 0) return
    this._body += '\n~~~~\n'
    if (this.description) this._body += `${this.description}\n`
    Object.keys(this.skills).forEach((skill) => {
      if (this.skills[skill].length == 0) return
      this._body += `+ ${skill}\n${this.skills[skill].map((skillItem) => `  - ${skillItem}\n`).join('')}`
    })
    this.length = this._body.split('\n').length
  }

  public get rawBody(): string {
    return this._body
  }

  public get body(): string {
    return this._body.trim()
  }

  public get toParams() {
    const answer_items = this.answerItems.map((answer_item) => answer_item.toParams)
    let body = this.questionBody
    const html_body = questionParser(body, this.fieldType, false)
    return {
      body,
      answer_items,
      type: this.fieldType,
      test: this.test,
      html_body,
      group: this.group,
      explanation: this.description
    }
  }

  public get questionBody(): string {
    return this.body.split(/^~~~+$/m)[0].trimRight()
  }

  public get metadataBody(): string {
    return (this.body.split(/^~~~+$/m)[1] || '').trimLeft()
  }

  public addSkillItem(skillName: string, criteria: string, name: string) {
    const skill = this.skills[skillName] || []
    skill.push({ name: name, criteria: criteria, answerItemsAttributes: [], promptAttributes: [] })
    // Vue 3: direct assignment instead of Vue.set
    this.skills[skillName] = skill
    this.updateBody()
  }

  public removeSkillItem(skillName: string, name: string) {
    const skill = this.skills[skillName] || []
    const index = skill.findIndex((item) => item.name == name)
    if (index < 0) {
      return
    }
    skill.splice(index, 1)
    if (skill.length > 0) {
      // Vue 3: direct assignment instead of Vue.set
      this.skills[skillName] = skill
    } else {
      // Vue 3: delete operator instead of Vue.delete
      delete this.skills[skillName]
    }
    this.updateBody()
  }

  public hasSkillItem(skillName: string, name: string) {
    const skill = this.skills[skillName]
    if (!skill) return false
    return this.skills[skillName].findIndex((item) => item.name == name) >= 0
  }

  private parseMetadata(body: string) {
    let lines = body.split('\n');
    let currentSkill: string;
    let match = null;
    let criteria = null;
    for (let i = 0; i < lines.length; i += 1) {
      if ((match = lines[i].match(/^\s*\+\s*(.+?)$/))) {
        if (lines[i].match(/^\s*\+\s*prompt_ref$/)) {
          break
        }
        currentSkill = match[1].trim();
        lines.splice(i, 1);
        i -= 1;
        let promptAttributes: Prompt[] = [];

        for (let j = i + 1; j < lines.length; j += 1) {
          if (lines[j].match(/^\s*\+\s*(.+?)$/)) {
            if (lines[j].match(/^\s*\+\s*prompt_ref$/)) {
              for (let k = j + 1; k < lines.length; k += 1) {
                if (lines[k].match(/^\s*\+\s*(.+?)$/)) {
                  break;
                }
                if ((match = lines[k].match(/^\s*\-\s*(?!-)(.+?)$/))) {
                  promptAttributes.push({ title: match[1].trim() });
                  lines.splice(k, 1);
                  k -= 1;
                }
              }
              lines.splice(j, 1);
              j -= 1;
            } else {
              break;
            }
          }
        }

        this.skills[currentSkill] = this.skills[currentSkill] || [];
        if (promptAttributes.length > 0) {
          this.skills[currentSkill].push({
            name: 'prompt_ref',
            promptAttributes: promptAttributes,
            criteria: null,
            answerItemsAttributes: []
          });
        }
      } else if (currentSkill && (match = lines[i].match(/^\s*\-\s*(?!-)(.+?)$/))) {
        const skillItem = match[1].trim();
        let realSkillItem = skillItem;
        let skillItemAnswerItems: AnswerItem[] = [];

        if (i + 1 < lines.length && (match = lines[i + 1].match(/^\s*\-\-\s*(.+?)$/))) {
          if (match[1].trim() === '"""') {
            let stringInline = ''
            for (let k = i + 2; k < lines.length; k += 1) {
              if (lines[k].trim() === '"""') {
                criteria = stringInline.trim();
                for (let l = k; l >= i + 1; l -= 1) {
                  lines.splice(l, 1);
                }
                break;
              } else {
                stringInline += lines[k].trim() + '\n';
              }
            }
          } else {
            criteria = match[1].trim();
            lines.splice(i + 1, 1);
          }
        }

        if (skillItem.includes(' $')) {
          const splitSkillItemName = skillItem.split(' $');
          realSkillItem = splitSkillItemName[0];
          let parser = new MarkdownQuestionParser(splitSkillItemName[1]);
          skillItemAnswerItems = parser.getTextareaAnswerItems();
        }

        lines.splice(i, 1);
        i -= 1;
        if (!realSkillItem) continue;

        this.skills[currentSkill] = this.skills[currentSkill] || [];
        this.skills[currentSkill].push({
          name: realSkillItem,
          criteria: criteria || null,
          answerItemsAttributes: skillItemAnswerItems,
          promptAttributes: []
        });

        criteria = null;
      } else if (lines[i].match(/^\s*(\+|\-)\s*$/)) {
        lines.splice(i, 1);
        i -= 1;
      }
    }
    this.description = lines.join('\n');
  }

  private parseQuestion(body: string) {
    var parser: MarkdownBodyParser
    parser = new MarkdownBodyParser(body)
    if (parser.fieldType) {
      this.fieldType = parser.fieldType
      this.test = parser.isTest
      this.textareaRowNumber = parser.textareaRowNumber
      this.answerItems = parser.answerItems
      this.formLineStart = parser.formLineStart
      this.formLineEnd = parser.formLineEnd
    }
  }

  public get type(): QuestionType {
    if (!this.fieldType) return null
    if (this.test) {
      return QUESTION_TEST_TYPES.find((questionType) => {
        return questionType.fieldType == this.fieldType
      })
    } else {
      return QUESTION_TEST_TYPES.find((questionType) => {
        return questionType.fieldType == this.fieldType
      })
    }
  }

  getScore(values: string[]): number {
    let score = 0
    const answers = this.answerItems.filter((item) => item.correct)
    if ((this.isCheckbox && values.length <= answers.length) || this.isRadio || this.isSelect || this.isTextarea) {
      values.forEach((value) => {
        const answer = answers.find((item) => item.body == value)
        if (answer) {
          score += answer.allotment
        }
      })
    } else if (this.isTextfield) {
      values.forEach((value, index) => {
        const answer = answers[index]
        if (answer) {
          // カンマ区切りの正解データをパース（引用符で囲まれた部分も正しく処理）
          const correctAnswers = CsvAnswerParser.parse(answer.body)
          if (correctAnswers.indexOf(value) >= 0) {
            score += answer.allotment
          }
        }
      })
    } else if (this.isInlineSelect) {
      values.forEach((value, index) => {
        const answerItemsScope = this.answerItemScopes[index]
        if (answerItemsScope) {
          const answer = answerItemsScope.find((item) => item.body == value)
          if (answer) {
            score += answer.allotment
          }
        }
      })
    } else if (this.isMultiCheckbox) {
      const scoreAnswer = answers.find((item) => item.name == 'point')
      const correctAnswers = answers.filter((item) => item.correct && item.name !== 'point')

      if (correctAnswers.length == values.length && correctAnswers.every(correctAnswer => {
        return values.includes(correctAnswer.body)
      })
      ) {
        score += scoreAnswer.allotment
      }
    }

    return score
  }

  getTotalScore() {
    let totalScore = 0
    const answers = this.answerItems.filter((item) => item.correct)

    if (this.isCheckbox || this.isTextfield) {
      answers.forEach((item) => {
        totalScore += item.allotment
      })
    } else if (this.isMultiCheckbox) {
      const scoreAnswer = answers.find((item) => item.name == 'point')
      if (scoreAnswer) {
        totalScore = scoreAnswer.allotment
      }
    } else if (this.isInlineSelect) {
      const scoreAnswer = this.answerItemScopes.forEach((answerItems) => {
        totalScore += max(answerItems.map((answerItem) => answerItem.allotment))
      })
    } else {
      answers.forEach((item) => {
        if (totalScore < item.allotment) {
          totalScore = item.allotment
        }
      })
    }

    return totalScore
  }

  get textAreaAnswerRaw(): string {
    if (!this.isTextarea || this.answerItems.length == 0) {
      return ''
    }

    const realAnswerItems = this.answerItems.filter((item) => item.name !== 'input')

    let rawText = '$('

    realAnswerItems.forEach((answerItem, index) => {
      rawText = `${rawText}[${answerItem.body}, ${answerItem.allotment}]`
      if (index < realAnswerItems.length - 1) {
        rawText += ', '
      }
    })

    rawText += ')'
    return rawText.replace(/,?\s?\[code.+]/, "")
  }

  get isTestQuestion(): boolean {
    return this.test
  }

  get isWorkSheet(): boolean {
    return !this.test && this.answerItems.length > 0
  }

  get isDescriptionQuestion(): boolean {
    return !this.test && this.answerItems.length == 0
  }
}
